import { useState, useEffect } from "react";
import { CalendarCheck, Star, Heart, Scissors, Share2, Check, Clock, MapPin, MoveRight } from "lucide-react";
import { FaWhatsapp, FaAmazon, FaInstagram } from "react-icons/fa";
import { LINKS, WHATSAPP_NUMBER, BUSINESS_NAME, openExternal, waIntent, IG_INTENT, WHATSAPP_DIGITS } from "@/constants/links";
import useFeatures from "@/constants/useFeatures";

const CARDS = [
  {
    testId: "info-whatsapp",
    href: LINKS.whatsapp,
    icon: FaWhatsapp,
    iconCls: "bg-[#25D366]/15 text-[#25D366]",
    title: "WhatsApp",
    value: WHATSAPP_NUMBER,
    sub: "Scrivimi per qualsiasi domanda",
    intent: waIntent(WHATSAPP_DIGITS),
  },
  {
    testId: "info-booking",
    href: LINKS.booking,
    icon: CalendarCheck,
    iconCls: "bg-emerald-500/15 text-emerald-400",
    title: "Prenotazioni",
    value: "SumUp Bookings",
    sub: "Scegli giorno e ora in pochi tap",
  },
  {
    testId: "info-reviews",
    href: LINKS.reviews,
    icon: Star,
    iconCls: "bg-blue-500/15 text-blue-400",
    title: "Recensioni",
    value: "Lascia la tua opinione",
    sub: "Il tuo parere conta tantissimo",
    intent: waIntent(WHATSAPP_DIGITS, "Ciao! Voglio lasciare una recensione del servizio"),
  },
  {
    testId: "info-instagram",
    href: "https://www.instagram.com/barberlatin_studio_jordan",
    icon: FaInstagram,
    iconCls: "bg-pink-500/15 text-pink-400",
    title: "Instagram",
    value: "@barberlatin_studio_jordan",
    sub: "Seguimi per foto e novità del salone",
    intent: IG_INTENT,
  },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function InfoPage() {
  const [copied, setCopied] = useState(false);
  const [address, setAddress] = useState("");
  const [hoursLabel, setHoursLabel] = useState("Mar – Sab · 9:00–13:30 / 15:30–20:30");
  const features = useFeatures();

  useEffect(() => {
    fetch(`${BACKEND}/api/settings/address`)
      .then((r) => r.json())
      .then((d) => setAddress(d.address || ""))
      .catch(() => {});
    fetch(`${BACKEND}/api/settings/hours`)
      .then((r) => r.json())
      .then((d) => setHoursLabel(`Mar – Sab · ${d.label.replace(" e ", " / ")}`))
      .catch(() => {});
  }, []);

  const shareApp = async () => {
    const data = {
      title: "Barber Latin",
      text: "Prenota il tuo appuntamento in salone e acquista i prodotti del barbiere su Amazon!",
      url: window.location.origin,
    };
    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(data.url);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = data.url;
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand("copy");
        } catch {}
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div data-testid="page-info" className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-300">
          <Scissors size={12} /> {BUSINESS_NAME}
        </span>
        <h2 className="font-display text-3xl font-bold text-white">Info &amp; Contatti</h2>
      </header>

      <div className="flex flex-col gap-3" data-testid="info-cards">
        <div
          data-testid="info-hours"
          className="flex items-center gap-4 rounded-2xl border border-amber-400/25 bg-amber-400/5 p-4"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-amber-400">
            <Clock size={22} />
          </span>
          <span className="flex-1">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Orari del salone
            </span>
            <span className="block text-sm font-bold text-white">{hoursLabel}</span>
            <span className="block text-xs text-zinc-400">Domenica e lunedì chiuso</span>
          </span>
        </div>
        {address && (
          <a
            data-testid="info-address"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 rounded-2xl border border-white/10 bg-zinc-900 p-4 transition-[border-color,transform] duration-200 hover:border-amber-400/40 active:scale-[0.97]"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-500/15 text-red-400">
              <MapPin size={22} />
            </span>
            <span className="flex-1">
              <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Indirizzo del salone
              </span>
              <span className="block text-sm font-bold text-white">{address}</span>
              <span className="block text-xs text-zinc-400">Tocca per aprire Google Maps</span>
            </span>
          </a>
        )}
        {CARDS.filter((c) =>
          (c.testId !== "info-booking" || features.sumup) &&
          (c.testId !== "info-whatsapp" || features.whatsapp)
        ).map(({ testId, href, icon: Icon, iconCls, title, value, sub, intent }) => (          <a
            key={testId}
            data-testid={testId}
            href={href}
            onClick={intent ? openExternal(href, intent) : undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 rounded-2xl border border-white/10 bg-zinc-900 p-4 transition-[border-color,transform] duration-200 hover:border-amber-400/40 active:scale-[0.97]"
          >
            <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconCls}`}>
              <Icon size={22} />
            </span>
            <span className="flex-1">
              <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                {title}
              </span>
              <span className="block text-sm font-bold text-white">{value}</span>
              <span className="block text-xs text-zinc-400">{sub}</span>
            </span>
          </a>
        ))}
      </div>

      <button
        data-testid="btn-share"
        onClick={shareApp}
        className="flex w-full items-center justify-center gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/10 px-5 py-4 text-sm font-extrabold uppercase tracking-widest text-amber-300 transition-[background-color,transform] duration-200 hover:bg-amber-400/20 active:scale-[0.97]"
      >
        {copied ? <Check size={18} /> : <Share2 size={18} />}
        {copied ? "Link copiato!" : "Condividi l'app"}
      </button>

      <section
        data-testid="thanks-section"
        className="flex flex-col items-center gap-3 rounded-2xl border border-amber-400/20 bg-amber-400/5 p-6 text-center"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/15 text-amber-400">
          <Heart size={22} fill="currentColor" />
        </span>
        <h3 className="font-display text-xl font-bold text-white">Grazie di cuore</h3>
        <p className="text-sm leading-relaxed text-zinc-300">
          Grazie per il vostro supporto e la collaborazione con il salone! Ogni gesto, una
          prenotazione o un acquisto tramite il link, ci aiuta a crescere.
        </p>
      </section>

      <section
        data-testid="qr-section"
        className="flex flex-col items-center gap-4 overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 pb-5"
      >
        <a
          data-testid="qr-link"
          href={LINKS.amazon}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full transition-[opacity,transform] duration-200 hover:opacity-90 active:scale-[0.99]"
        >
          <img
            src="/qr-amazon.png"
            alt="QR code del link affiliato Amazon di Barber Latin"
            loading="lazy"
            className="w-full object-cover"
          />
        </a>
        <a
          data-testid="btn-qr-amazon"
          href={LINKS.amazon}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-[calc(100%-2.5rem)] items-center justify-center gap-2 rounded-xl bg-amber-400 px-5 py-3.5 text-sm font-extrabold text-zinc-950 shadow-[0_0_20px_rgba(251,191,36,0.25)] transition-[background-color,transform] duration-200 hover:bg-amber-300 active:scale-[0.97]"
        >
          <FaAmazon size={18} /> Apri su Amazon
        </a>
      </section>

      <p className="text-xs leading-relaxed text-zinc-500" data-testid="affiliate-disclosure-info">
        In qualità di Affiliato Amazon, potrei ricevere una commissione dagli acquisti idonei
        effettuati tramite questo link, senza costi aggiuntivi per te.
      </p>

      <div className="mx-auto flex items-center justify-center gap-4">
        <a
          data-testid="link-privacy"
          href="/privacy"
          className="text-[10px] font-semibold uppercase tracking-widest text-zinc-700 transition-colors duration-200 hover:text-amber-400"
        >
          Privacy Policy
        </a>
        <a
          data-testid="link-admin"
          href="/admin"
          className="text-[10px] font-semibold uppercase tracking-widest text-zinc-700 transition-colors duration-200 hover:text-amber-400"
        >
          Area riservata
        </a>
      </div>
    </div>
  );
}
